import { ChangeDetectorRef, Injectable } from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatCalendar } from '@angular/material/datepicker';
import { Observable, ReplaySubject } from 'rxjs';
import * as constants from 'src/app/common/constant/constants.type';
import { LoaderService } from 'src/app/shared/loader/loader.service';
import { AppointmentService } from './appointment.service';
import { AppointmentSlot } from './iappointment';

export interface SlotResult {
  type: 'slots' | 'nextAvailable' | 'noAvailability' | 'error';
  slots?: AppointmentSlot[];
  daysAvailable?: string[];
  selectedDate?: Date;
  formattedDate?: string | null;
}

@Injectable()
export class AppointmentCalendarService {

  private unavailableDays: string[] = [];
  private bookedDatesSet = new Set<string>();
  private lastFetchedMonth?: number;
  private lastFetchedYear?: number;

  private readonly dayMap: Record<string, number> = {
    sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6,
  };

  constructor(
    private datePipe: DatePipe,
    private appointmentService: AppointmentService,
    private loaderService: LoaderService
  ) { }

  // ── Date filter & class (read live state on each calendar cell render) ──

  dateFilter = (date: Date | null): boolean => {
    if (!date) return false;
    if (this.unavailableDays.length === 0) return true;
    const dayOfWeek = date.getDay();
    return !this.unavailableDays.some(day => this.dayMap[day.toLowerCase()] === dayOfWeek);
  };

  dateClass = (date: Date): string => {
    return this.bookedDatesSet.has(this.formatDateKey(date)) ? 'booked-date' : '';
  };

  // ── Calendar manipulation ──

  refreshCalendar(calendar: MatCalendar<Date>, cdr: ChangeDetectorRef) {
    if (!calendar) return;
    calendar.updateTodaysDate();
    cdr.detectChanges();
  }

  resetCalendarToDate(calendar: MatCalendar<Date>, date: Date, cdr: ChangeDetectorRef) {
    if (!calendar) return;
    calendar.activeDate = date;
    calendar.updateTodaysDate();
    cdr.detectChanges();
  }

  updateCalendarActiveDate(calendar: MatCalendar<Date>, date: Date, cdr: ChangeDetectorRef) {
    if (!calendar) return;
    calendar.activeDate = new Date(date);
    calendar.updateTodaysDate();
    cdr.detectChanges();
  }

  resetBookedDaysCache() {
    this.lastFetchedMonth = undefined;
    this.lastFetchedYear = undefined;
  }

  // ── Slot fetching ──

  fetchSlots(date: Date, doctorId: number): Observable<SlotResult> {
    const result$ = new ReplaySubject<SlotResult>(1);
    const formattedDate = this.datePipe.transform(date, constants.DATE_FORMAT);
    const request = { date: formattedDate, doctorId };

    this.loaderService.show();
    this.appointmentService.getAppointmentSlot(request).subscribe({
      next: (data) => {
        const slots = data?.result?.timeSlots;
        if (slots?.length > 0) {
          this.applyUnavailableDays(data.result.daysAvailable);
          this.loaderService.hide();
          result$.next({ type: 'slots', slots, daysAvailable: data.result.daysAvailable });
          result$.complete();
        } else {
          this.searchNextAvailableDate(new Date(date), doctorId, 0, result$);
        }
      },
      error: () => {
        this.unavailableDays = [];

        this.loaderService.hide();
        result$.next({ type: 'error' });
        result$.complete();
      }
    });

    return result$.asObservable();
  }

  refreshSlots(selectedDate: Date, doctorId: number): Observable<any[]> {
    const result$ = new ReplaySubject<any[]>(1);
    const request = { date: selectedDate, doctorId };

    this.appointmentService.getAppointmentSlot(request).subscribe((data) => {
      result$.next(data.result.timeSlots);
      result$.complete();
    });

    return result$.asObservable();
  }

  /** Returns true if data was fetched, false if cache hit (no refresh needed) */
  fetchBookedDays(doctorId: number, activeDate: Date): Observable<boolean> {
    const done$ = new ReplaySubject<boolean>(1);

    if (!doctorId) {
      done$.next(false);
      done$.complete();
      return done$.asObservable();
    }

    const month = activeDate.getMonth() + 1;
    const year = activeDate.getFullYear();

    if (this.lastFetchedMonth === month && this.lastFetchedYear === year) {
      done$.next(false);
      done$.complete();
      return done$.asObservable();
    }

    this.lastFetchedMonth = month;
    this.lastFetchedYear = year;

    this.appointmentService.getBookedDays(doctorId, month, year).subscribe({
      next: (resp: any) => {
        this.bookedDatesSet = this.parseBookedDays(resp);
        done$.next(true);
        done$.complete();
      },
      error: () => {
        this.bookedDatesSet = new Set();
        done$.next(true);
        done$.complete();
      }
    });

    return done$.asObservable();
  }

  // ── Private ──

  private searchNextAvailableDate(nextDate: Date, doctorId: number, daysChecked: number, result$: ReplaySubject<SlotResult>) {
    const MAX_DAYS = 14;

    if (daysChecked >= MAX_DAYS) {
      this.unavailableDays = [];
      this.loaderService.hide();
      result$.next({ type: 'noAvailability' });
      result$.complete();
      return;
    }

    nextDate.setDate(nextDate.getDate() + 1);
    const formattedDate = this.datePipe.transform(nextDate, constants.DATE_FORMAT);
    const request = { date: formattedDate, doctorId };

    this.appointmentService.getAppointmentSlot(request).subscribe({
      next: (data) => {
        const slots = data?.result?.timeSlots;
        if (slots?.length > 0) {
          this.applyUnavailableDays(data.result.daysAvailable);
          this.loaderService.hide();
          result$.next({
            type: 'nextAvailable', slots,
            daysAvailable: data.result.daysAvailable,
            selectedDate: new Date(nextDate), formattedDate
          });
          result$.complete();
        } else {
          this.searchNextAvailableDate(nextDate, doctorId, daysChecked + 1, result$);
        }
      },
      error: () => {
        this.unavailableDays = [];

        this.loaderService.hide();
        result$.next({ type: 'error' });
        result$.complete();
      }
    });
  }

  private applyUnavailableDays(availableDays: string[]) {
    if (availableDays?.length > 0) {
      const lower = availableDays.map(a => a.toLowerCase());
      this.unavailableDays = constants.DAYS.filter(d => !lower.includes(d.toLowerCase()));
    } else {
      this.unavailableDays = [];
    }
  }

  private parseBookedDays(resp: any): Set<string> {
    const result = resp?.result ?? resp;
    const arr = result?.bookedDays ?? result ?? [];
    const dateSet = new Set<string>();

    arr.forEach((item: any) => {
      const d = item?.date ? new Date(item.date) : (item instanceof Date ? item : null);
      if (d) dateSet.add(this.formatDateKey(d));
    });

    return dateSet;
  }

  private formatDateKey(date: Date): string {
    const y = date.getFullYear();
    const m = (date.getMonth() + 1).toString().padStart(2, '0');
    const d = date.getDate().toString().padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
}
